import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft,
  IconCheck,
  IconAlertTriangle,
  IconLock,
  IconDownload,
} from '@tabler/icons-react';

export default function RiskAssessmentDetail() {
  const { language } = useTranslation();
  const { raId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile, role } = useAuth();
  const [ra, setRa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !raId) return;
      try {
        const snap = await getDoc(doc(db, 'organisations', orgId, 'riskAssessments', raId));
        if (snap.exists()) setRa({ id: snap.id, ...snap.data() });
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId, raId]);

  const handleActivate = async () => {
    setActivating(true);
    try {
      await updateDoc(doc(db, 'organisations', orgId, 'riskAssessments', raId), {
        status: 'active',
        sealed: true,
        sealedAt: serverTimestamp(),
        sealedBy: userProfile?.name,
        updatedAt: serverTimestamp(),
      });
      setRa(prev => ({ ...prev, status: 'active', sealed: true, sealedBy: userProfile?.name }));
      toast.success('Risk assessment activated and sealed');
    } catch (err) {
      toast.error('Failed to activate risk assessment');
    }
    setActivating(false);
  };

  const getRiskColour = (rating) => {
    if (rating >= 15) return 'var(--color-danger)';
    if (rating >= 8) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const getRiskLabel = (rating) => {
    if (rating >= 15) return 'High';
    if (rating >= 8) return 'Medium';
    return 'Low';
  };

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString(language, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <Layout><div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div></Layout>;

  if (!ra) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/risk-assessments')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Risk assessments
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>Risk assessment not found</div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 720, margin: '0 auto' }}>

        <div onClick={() => navigate('/risk-assessments')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Risk assessments
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h1 style={{ fontSize: 20, fontWeight: 400 }}>{ra.title || 'Risk assessment'}</h1>
              <StatusPill status={ra.status || 'draft'} size="lg" />
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', textTransform: 'capitalize' }}>
              {ra.type} · Assessed by {ra.assessedBy || '—'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" size="sm" icon={<IconDownload size={13} stroke={1.5} />} onClick={() => toast.info('PDF export coming soon')}>
              Export PDF
            </Button>
            {ra.status === 'draft' && (role === 'org_admin' || role === 'manager') && (
              <Button
                variant="primary"
                size="sm"
                icon={<IconLock size={13} stroke={1.5} />}
                onClick={handleActivate}
                disabled={activating}
                loading={activating}
              >
                Activate & seal
              </Button>
            )}
          </div>
        </div>

        {ra.sealed && (
          <div style={{ background: '#E6F1FB', border: '0.5px solid #185FA5', borderRadius: 'var(--radius-lg)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <IconLock size={14} color="#185FA5" stroke={1.5} />
            <div style={{ fontSize: 12, color: '#185FA5' }}>
              Sealed by {ra.sealedBy} · {formatDateTime(ra.sealedAt)}
            </div>
          </div>
        )}

        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Assessment details</div>
          {[
            { label: 'Title',       value: ra.title || '—'                     },
            { label: 'Type',        value: ra.type || '—', capitalise: true     },
            { label: 'Assessed by', value: ra.assessedBy || '—'                },
            { label: 'Assessed at', value: formatDateTime(ra.assessedAt)        },
            { label: 'Review date', value: ra.reviewDate || '—'                },
            { label: 'Machine',     value: ra.machineName || '—'               },
            { label: 'Site',        value: ra.siteName || '—'                  },
          ].map(row => (
            <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{row.label}</span>
              <span style={{ fontSize: 13, color: 'var(--color-text-primary)', textTransform: row.capitalise ? 'capitalize' : 'none' }}>{row.value}</span>
            </div>
          ))}
        </Card>

        {ra.hazards?.length > 0 && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 12 }}>Hazards ({ra.hazards.length})</div>
            {ra.hazards.map((hazard, i) => {
              const rating = (hazard.likelihood || 1) * (hazard.severity || 1);
              const colour = getRiskColour(rating);
              return (
                <Card key={i} style={{ marginBottom: 10, border: rating >= 15 ? '0.5px solid var(--color-danger)' : '0.5px solid var(--color-border-light)' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 4 }}>{hazard.hazard}</div>
                      {hazard.whoIsAtRisk && (
                        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Who is at risk: {hazard.whoIsAtRisk}</div>
                      )}
                    </div>
                    <div style={{ flexShrink: 0, marginLeft: 16, textAlign: 'right' }}>
                      <div style={{ fontSize: 22, fontWeight: 400, color: colour, lineHeight: 1 }}>{rating}</div>
                      <div style={{ fontSize: 10, color: colour, marginTop: 2 }}>{getRiskLabel(rating)}</div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                    <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', padding: '8px 12px' }}>
                      <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>LIKELIHOOD</div>
                      <div style={{ fontSize: 16, fontWeight: 400, color: 'var(--color-text-primary)' }}>{hazard.likelihood || '—'} / 5</div>
                    </div>
                    <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', padding: '8px 12px' }}>
                      <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>SEVERITY</div>
                      <div style={{ fontSize: 16, fontWeight: 400, color: 'var(--color-text-primary)' }}>{hazard.severity || '—'} / 5</div>
                    </div>
                  </div>

                  {hazard.existingControls && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>Existing controls</div>
                      <div style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.5 }}>{hazard.existingControls}</div>
                    </div>
                  )}

                  {hazard.additionalControls && (
                    <div style={{ padding: '8px 12px', background: rating >= 15 ? 'var(--color-danger-light)' : 'var(--color-warning-light)', borderRadius: 'var(--radius-md)' }}>
                      <div style={{ fontSize: 11, color: rating >= 15 ? 'var(--color-danger)' : 'var(--color-warning)', marginBottom: 4 }}>Additional controls required</div>
                      <div style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.5 }}>{hazard.additionalControls}</div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {!ra.hazards?.length && (
          <Card>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', textAlign: 'center', padding: '20px 0' }}>
              No hazards recorded in this assessment
            </div>
          </Card>
        )}

      </div>
    </Layout>
  );
}